// Shared simple stat tile (title + big number + subtitle + icon badge) used by every
// department dashboard's summary-count row (HR, Store and Purchase, etc.).
import React from 'react';
import { Loader2 } from 'lucide-react';

export const TILE_TONES = {
    blue:   { icon: 'bg-blue-100 dark:bg-blue-900/30',     text: 'text-blue-600 dark:text-blue-500' },
    orange: { icon: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-500' },
    red:    { icon: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-600 dark:text-red-500' },
    green:  { icon: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-600 dark:text-green-500' },
    purple: { icon: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-500' },
};

const StatTile = ({ title, subtitle, icon: Icon, tone, loading, value, onClick }) => {
    const t = TILE_TONES[tone] || TILE_TONES.blue;
    const clickable = typeof onClick === 'function';
    const Wrapper = clickable ? 'button' : 'div';
    return (
        <Wrapper
            onClick={onClick}
            className={`w-full text-left bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-xl transition-all ${clickable ? 'cursor-pointer hover:border-orange-300 dark:hover:border-orange-700' : ''}`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {loading ? <Loader2 className={`w-5 h-5 animate-spin ${t.text}`} /> : (value ?? 0)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${t.icon}`}>
                    <Icon className={`w-6 h-6 ${t.text}`} />
                </div>
            </div>
        </Wrapper>
    );
};

export default StatTile;

// Compact tile (small title, icon badge, big number, subtitle) — the secondary tiles beside a
// dashboard's headline tile (HR, Accounts). Clickable only when onClick is passed.
export const MiniStatTile = ({ title, subtitle, icon: Icon, value, loading, onClick }) => {
    const clickable = typeof onClick === 'function';
    const Wrapper = clickable ? 'button' : 'div';
    return (
        <Wrapper
            onClick={onClick}
            className={`text-left bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 flex flex-col justify-between ${clickable ? 'hover:shadow-lg dark:hover:shadow-xl hover:border-orange-300 dark:hover:border-orange-700 transition-all cursor-pointer' : ''
                }`}
        >
            <div className="flex items-start justify-between">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                </div>
            </div>
            <div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-500" /> : (value ?? 0)}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>
            </div>
        </Wrapper>
    );
};
